import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useFeatureFlags } from "../../context/FeatureFlagsProvider"
import { ChaptersDropdown, isChaptersNavActive } from "./LandingNavChaptersDropdown"

type NavLinkItem = {
  href: string
  label: string
  accent?: boolean
  external?: boolean
}

const isLinkActive = (pathname: string, href: string) => {
  if (href === "/") {
    return pathname === "/"
  }
  return pathname === href || pathname.startsWith(`${href}/`)
}

const linkClassName = (active: boolean, accent?: boolean, mobile = false) => {
  const size = mobile ? "text-base" : "text-sm"

  if (accent) {
    return [
      "font-dm-sans font-semibold tracking-wide text-[#9e7e43] transition-all",
      size,
      active ? "opacity-100 underline decoration-2 underline-offset-4" : "opacity-90 hover:opacity-100",
    ].join(" ")
  }

  return [
    "font-dm-sans tracking-wide text-white transition-all",
    size,
    active
      ? "font-semibold opacity-100 underline decoration-[#9e7e43] decoration-2 underline-offset-4"
      : "font-medium opacity-90 hover:opacity-100",
  ].join(" ")
}

const LandingNav: React.FC = () => {
  const location = useLocation()
  const { flags } = useFeatureFlags()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [fastTransition, setFastTransition] = useState(true)
  const lastScrollY = useRef(0)

  const navLinks = useMemo<NavLinkItem[]>(
    () => [
      { href: "/", label: "Home" },
      ...(flags.announcements ? [{ href: "/news", label: "News" }] : []),
      { href: "/about", label: "About Us" },
      { href: "/give", label: "Give", accent: true },
    ],
    [flags.announcements]
  )

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      const scrollingUp = y < lastScrollY.current
      lastScrollY.current = y
      setScrolled(y > 16)
      setFastTransition(y <= 16 || scrollingUp)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const transitionClass = fastTransition ? "duration-75" : "duration-150"

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 transition-all ${transitionClass} ${
        scrolled
          ? "border-b border-white/10 bg-[rgba(13,13,13,0.72)] shadow-lg backdrop-blur-xl"
          : "bg-transparent px-4 py-3 md:px-8 lg:px-20"
      }`}
    >
      <nav
        className={`relative mx-auto flex w-full items-center justify-between transition-all ${transitionClass} ${
          scrolled
            ? "max-w-[1243px] px-4 py-2.5 md:px-8 lg:px-20"
            : "max-w-[1243px] rounded-xl border-y border-[#b79591]/30 bg-white/10 px-4 py-2 backdrop-blur-sm md:px-6"
        }`}
      >
        <Link to="/" className="flex shrink-0 items-center gap-2.5 sm:gap-3">
          <img
            src="/dominion-city-logo.png"
            alt=""
            className="h-8 w-auto object-contain md:h-10"
          />
          <div className="leading-tight">
            <p className="font-outfit text-xs font-semibold uppercase tracking-wide text-white sm:text-sm">
              Dominion City
            </p>
            <p className="font-outfit text-[10px] font-medium uppercase tracking-[0.18em] text-[#9e7e43] sm:text-xs">
              Ireland
            </p>
          </div>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          <ul className="flex items-center gap-4">
            {navLinks.slice(0, 2).map(({ href, label, accent, external }) => {
              const active = !external && isLinkActive(location.pathname, href)

              return (
                <li key={href}>
                  {external ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClassName(false, accent)}
                    >
                      {label}
                    </a>
                  ) : (
                    <Link to={href} className={linkClassName(active, accent)}>
                      {label}
                    </Link>
                  )}
                </li>
              )
            })}
            <ChaptersDropdown active={isChaptersNavActive(location.pathname)} />
            {navLinks.slice(2).map(({ href, label, accent, external }) => {
              const active = !external && isLinkActive(location.pathname, href)

              return (
                <li key={href}>
                  {external ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClassName(false, accent)}
                    >
                      {label}
                    </a>
                  ) : (
                    <Link to={href} className={linkClassName(active, accent)}>
                      {label}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
        </div>

        <button
          type="button"
          className="flex items-center justify-center p-1.5 text-white md:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {menuOpen && (
          <div className="absolute left-0 right-0 top-full z-20 mt-2 flex flex-col gap-3 rounded-xl border border-[#b79591]/30 bg-[rgba(13,13,13,0.95)] p-4 backdrop-blur-md md:hidden">
            <ul className="flex flex-col gap-3">
              {navLinks.slice(0, 2).map(({ href, label, accent, external }) => {
                const active = !external && isLinkActive(location.pathname, href)

                return (
                  <li key={href}>
                    {external ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName(false, accent, true)}
                        onClick={() => setMenuOpen(false)}
                      >
                        {label}
                      </a>
                    ) : (
                      <Link
                        to={href}
                        className={linkClassName(active, accent, true)}
                        onClick={() => setMenuOpen(false)}
                      >
                        {label}
                      </Link>
                    )}
                  </li>
                )
              })}
              <ChaptersDropdown
                active={isChaptersNavActive(location.pathname)}
                mobile
                onNavigate={() => setMenuOpen(false)}
              />
              {navLinks.slice(2).map(({ href, label, accent, external }) => {
                const active = !external && isLinkActive(location.pathname, href)

                return (
                  <li key={href}>
                    {external ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName(false, accent, true)}
                        onClick={() => setMenuOpen(false)}
                      >
                        {label}
                      </a>
                    ) : (
                      <Link
                        to={href}
                        className={linkClassName(active, accent, true)}
                        onClick={() => setMenuOpen(false)}
                      >
                        {label}
                      </Link>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </nav>
    </header>
  )
}

export default LandingNav
